<script lang="ts" setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useUserStore, PartyType } from "@/stores/user";

const router = useRouter();
const userStore = useUserStore();

const commandMenuOpen = ref(false);
const commandSearch = ref("");
const commandSearchField = ref<any>(null);
const activeCommandIndex = ref(-1);
const commandListContainer = ref<any>(null);

const isMacPlatform = /Mac|iPhone|iPad/.test(navigator.platform);
const commandShortcutText = computed(() => (isMacPlatform ? "Cmd+K" : "Ctrl+K"));

const CommandSection = {
  Navigation: "navigatie",
  Actions: "acties",
  Projects: "partijen",
} as const;

type CommandSectionType = (typeof CommandSection)[keyof typeof CommandSection];


function compareName(a: any, b: any): number {
  const itemA = a.name.toUpperCase();
  const itemB = b.name.toUpperCase();

  if (itemA > itemB) {
    return 1;
  }

  if (itemA < itemB) {
    return -1;
  }

  return 0;
}

const PARTY_TYPE_CONFIG: Record<string, { icon: string | null }> = {
  [PartyType.EnergyLabels]: { icon: "mdi-label-outline" },
  [PartyType.Homes]: { icon: "mdi-home-outline" },
  [PartyType.Utility]: { icon: "mdi-office-building-outline" },
};

function partyIcon(partyType: string | number): string | null {
  return PARTY_TYPE_CONFIG[String(partyType)]?.icon ?? null;
}

function canAccessRoute(routeName: string): boolean {
  const route = router.getRoutes().find((r) => r.name === routeName);
  if (!route) {
    return true;
  }

  const allowedFor = route.meta?.allowedFor as string[] | undefined;
  if (allowedFor && allowedFor.length > 0) {
    const currentPartyType = String(userStore.currentPartyType);
    const isUtilityAllowed = allowedFor.includes(PartyType.Utility) || (allowedFor.includes(PartyType.None) && userStore.isPartyUtility);
    const isHomesAllowed = allowedFor.includes(PartyType.Homes);
    const isLabelsAllowed = allowedFor.includes(PartyType.EnergyLabels);

    const isAllowedByPartyType =
      (userStore.isPartyUtility && isUtilityAllowed) ||
      (userStore.isPartyHome && isHomesAllowed) ||
      (userStore.isPartyEnergyLabels && isLabelsAllowed) ||
      allowedFor.includes(currentPartyType);

    if (!isAllowedByPartyType) {
      return false;
    }
  }

  const authorize = route.meta?.authorize as string[] | undefined;
  if (authorize && authorize.length > 0 && !userStore.matchingProjectPermission(authorize)) {
    return false;
  }

  return true;
}

type RouteTarget = {
  name: string;
  params?: Record<string, any>;
};

type CommandEntry = {
  id: string;
  section: CommandSectionType;
  label: string;
  icon: string;
  keywords: string;
  order?: number;
  route?: RouteTarget;
  projectReference?: string;
};

const baseRouteParams = computed(() => ({
  partyReference: userStore.currentPartyReference,
}));

// Derive command items from router's commandSearchMenu metadata
const commandNavigationItems = computed<CommandEntry[]>(() => {
  const items: CommandEntry[] = [];
  const allRoutes = router.getRoutes();

  allRoutes.forEach((route) => {
    const commandMeta = (route.meta?.commandSearchMenu as any) ?? null;
    if (!commandMeta || commandMeta.section !== CommandSection.Navigation) {
      return;
    }

    const routeName = route.name as string;
    if (!canAccessRoute(routeName)) {
      return;
    }

    items.push({
      id: `nav-${routeName}`,
      section: CommandSection.Navigation,
      label: commandMeta.label,
      icon: commandMeta.icon,
      keywords: commandMeta.keywords,
      order: Number(commandMeta.order ?? 9999),
      route:
        routeName === "Info"
          ? { name: routeName }
          : { name: routeName, params: baseRouteParams.value },
    });
  });

  return items.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999) || a.label.localeCompare(b.label));
});

const commandActionItems = computed<CommandEntry[]>(() => {
  const items: CommandEntry[] = [];
  const allRoutes = router.getRoutes();

  allRoutes.forEach((route) => {
    const commandMeta = (route.meta?.commandSearchMenu as any) ?? null;
    if (!commandMeta || commandMeta.section !== CommandSection.Actions) {
      return;
    }

    const routeName = route.name as string;
    if (!canAccessRoute(routeName)) {
      return;
    }

    items.push({
      id: `action-${routeName}`,
      section: CommandSection.Actions,
      label: commandMeta.label,
      icon: commandMeta.icon,
      keywords: commandMeta.keywords,
      order: Number(commandMeta.order ?? 9999),
      route: { name: routeName, params: baseRouteParams.value },
    });
  });

  return items.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999) || a.label.localeCompare(b.label));
});

const commandProjectItems = computed<CommandEntry[]>(() => {
  const allProjects = userStore.getAllProjecs
    .map((el: any) => ({
      reference: el.partyReference,
      name: el.description,
      partyType: el.partyTypeKey,
      isCurrent: userStore.currentPartyReference === el.partyReference,
    }))
    .sort(compareName);

  const current = allProjects.find((project) => project.isCurrent);
  const others = allProjects.filter((project) => !project.isCurrent);
  const sorted = current ? [current, ...others] : others;

  return sorted.map((project) => ({
    id: `project-${project.reference}`,
    section: CommandSection.Projects,
    label: project.name,
    icon: partyIcon(project.partyType) ?? "mdi-domain",
    keywords: `${project.name} project partij party switch`,
    projectReference: String(project.reference),
  }));
});

const commandEntries = computed(() => [
  ...commandNavigationItems.value,
  ...commandActionItems.value,
  ...commandProjectItems.value,
]);

const normalizedCommandSearch = computed(() => commandSearch.value.trim().toLowerCase());

const filteredCommandEntries = computed(() => {
  if (!normalizedCommandSearch.value) {
    return commandEntries.value;
  }

  return commandEntries.value.filter((entry) => {
    const haystack = `${entry.label} ${entry.keywords}`.toLowerCase();
    return haystack.includes(normalizedCommandSearch.value);
  });
});

const filteredNavigationEntries = computed(() => filteredCommandEntries.value.filter((entry) => entry.section === CommandSection.Navigation));
const filteredActionEntries = computed(() => filteredCommandEntries.value.filter((entry) => entry.section === CommandSection.Actions));
const filteredProjectEntries = computed(() => filteredCommandEntries.value.filter((entry) => entry.section === CommandSection.Projects));
const flatFilteredCommandEntries = computed(() => [
  ...filteredNavigationEntries.value,
  ...filteredActionEntries.value,
  ...filteredProjectEntries.value,
]);
const actionSectionOffset = computed(() => filteredNavigationEntries.value.length);
const projectSectionOffset = computed(() => filteredNavigationEntries.value.length + filteredActionEntries.value.length);

const hasCommandResults = computed(
  () => filteredNavigationEntries.value.length > 0 || filteredActionEntries.value.length > 0 || filteredProjectEntries.value.length > 0
);

function openCommandMenu() {
  commandMenuOpen.value = true;
}

function closeCommandMenu() {
  commandMenuOpen.value = false;
  commandSearch.value = "";
  activeCommandIndex.value = -1;
}

async function switchProject(partyReference: string) {
  closeCommandMenu();
  await router.push({ name: "Dashboard", params: { partyReference } });
}

async function executeCommandEntry(entry: CommandEntry) {
  if (entry.projectReference) {
    await switchProject(entry.projectReference);
    return;
  }

  if (entry.route) {
    closeCommandMenu();
    await router.push(entry.route);
  }
}

function onCommandShortcut(event: KeyboardEvent) {
  const isK = event.key.toLowerCase() === "k";
  const hasModifier = event.ctrlKey || event.metaKey;
  if (!isK || !hasModifier) {
    return;
  }

  event.preventDefault();
  if (commandMenuOpen.value) {
    closeCommandMenu();
  } else {
    openCommandMenu();
  }
}

function moveActiveCommand(step: number) {
  const total = flatFilteredCommandEntries.value.length;
  if (total === 0) {
    activeCommandIndex.value = -1;
    return;
  }

  if (activeCommandIndex.value === -1) {
    activeCommandIndex.value = step > 0 ? 0 : total - 1;
    return;
  }

  const next = (activeCommandIndex.value + step + total) % total;
  activeCommandIndex.value = next;
}

async function executeActiveCommand() {
  if (activeCommandIndex.value < 0 || activeCommandIndex.value >= flatFilteredCommandEntries.value.length) {
    return;
  }

  await executeCommandEntry(flatFilteredCommandEntries.value[activeCommandIndex.value]);
}

function onCommandPaletteKeydown(event: KeyboardEvent) {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    moveActiveCommand(1);
    return;
  }

  if (event.key === "ArrowUp") {
    event.preventDefault();
    moveActiveCommand(-1);
    return;
  }

  if (event.key === "Enter") {
    event.preventDefault();
    void executeActiveCommand();
    return;
  }

  if (event.key === "Escape") {
    event.preventDefault();
    closeCommandMenu();
  }
}

function scrollActiveCommandIntoView() {
  if (!commandMenuOpen.value || activeCommandIndex.value < 0) {
    return;
  }

  nextTick(() => {
    const containerRef = commandListContainer.value;
    const container = containerRef?.$el ?? containerRef;
    if (!(container instanceof HTMLElement)) {
      return;
    }

    const activeItem = container.querySelector(`[data-command-index="${activeCommandIndex.value}"]`) as HTMLElement | null;
    if (!activeItem) {
      return;
    }

    activeItem.scrollIntoView({ block: "nearest" });
  });
}

watch(commandMenuOpen, async (isOpen) => {
  if (!isOpen) {
    commandSearch.value = "";
    activeCommandIndex.value = -1;
    return;
  }

  await nextTick();
  activeCommandIndex.value = flatFilteredCommandEntries.value.length > 0 ? 0 : -1;
  setTimeout(() => commandSearchField.value?.focus(), 50);
});

watch(flatFilteredCommandEntries, (entries) => {
  if (!commandMenuOpen.value) {
    return;
  }

  if (entries.length === 0) {
    activeCommandIndex.value = -1;
    return;
  }

  if (activeCommandIndex.value < 0 || activeCommandIndex.value >= entries.length) {
    activeCommandIndex.value = 0;
  }
});

watch(activeCommandIndex, () => {
  scrollActiveCommandIntoView();
});

onMounted(() => {
  window.addEventListener("keydown", onCommandShortcut);
});

onUnmounted(() => {
  window.removeEventListener("keydown", onCommandShortcut);
});
</script>

<template>
  <v-btn
    variant="outlined"
    rounded="lg"
    class="project-switcher-activator command-menu-activator mr-3 d-none d-md-flex"
    prepend-icon="mdi-magnify"
    @click="openCommandMenu"
  >
    <span class="command-trigger-text">{{ commandShortcutText }}</span>
  </v-btn>

  <v-dialog v-model="commandMenuOpen" max-width="760" width="95vw" class="command-palette-dialog" @keydown="onCommandPaletteKeydown">
    <v-card rounded="lg" class="command-palette-card">
      <div class="command-palette-header">
        <v-text-field
          ref="commandSearchField"
          v-model="commandSearch"
          variant="plain"
          hide-details
          density="comfortable"
          prepend-inner-icon="mdi-magnify"
          placeholder="Type a command or search..."
          class="px-4 pt-3 command-palette-search"
        >
        </v-text-field>
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          class="command-palette-close"
          @click="closeCommandMenu"
          aria-label="Sluiten"
        ></v-btn>
      </div>

      <v-divider></v-divider>

      <v-card-text ref="commandListContainer" class="pa-2 command-palette-content">
        <template v-if="hasCommandResults">
          <div v-if="filteredNavigationEntries.length" class="command-section">
            <p class="command-section-title">Navigatie</p>
            <v-list density="comfortable" nav>
              <v-list-item
                v-for="(entry, index) in filteredNavigationEntries"
                :key="entry.id"
                rounded="lg"
                :active="activeCommandIndex === index"
                :data-command-index="index"
                @click="executeCommandEntry(entry)"
                @mouseenter="activeCommandIndex = index"
              >
                <template #prepend>
                  <v-icon :icon="entry.icon"></v-icon>
                </template>
                <v-list-item-title>{{ entry.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </div>

          <div v-if="filteredActionEntries.length" class="command-section">
            <p class="command-section-title">Acties</p>
            <v-list density="comfortable" nav>
              <v-list-item
                v-for="(entry, index) in filteredActionEntries"
                :key="entry.id"
                rounded="lg"
                :active="activeCommandIndex === actionSectionOffset + index"
                :data-command-index="actionSectionOffset + index"
                @click="executeCommandEntry(entry)"
                @mouseenter="activeCommandIndex = actionSectionOffset + index"
              >
                <template #prepend>
                  <v-icon :icon="entry.icon"></v-icon>
                </template>
                <v-list-item-title>{{ entry.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </div>

          <div v-if="filteredProjectEntries.length" class="command-section">
            <p class="command-section-title">Partijen</p>
            <v-list density="comfortable" nav>
              <v-list-item
                v-for="(entry, index) in filteredProjectEntries"
                :key="entry.id"
                rounded="lg"
                :active="activeCommandIndex === projectSectionOffset + index"
                :data-command-index="projectSectionOffset + index"
                @click="executeCommandEntry(entry)"
                @mouseenter="activeCommandIndex = projectSectionOffset + index"
              >
                <template #prepend>
                  <v-icon :icon="entry.icon"></v-icon>
                </template>
                <v-list-item-title>{{ entry.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </div>
        </template>

        <div v-else class="px-4 py-6 text-medium-emphasis">Geen resultaten gevonden.</div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.command-palette-content {
  max-height: 70vh;
  overflow-y: auto;
}

.command-palette-header {
  position: relative;
}

.command-palette-search {
  padding-right: 44px !important;
}

.command-palette-close {
  position: absolute;
  top: 10px;
  right: 10px;
}

.command-section-title {
  margin: 10px 14px 4px;
  font-size: 0.88rem;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
}

.command-trigger-text {
  letter-spacing: 0.02em;
}
</style>

